import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { DigitalScoreRing, DigitalTierBadge } from '../components/DigitalScore';
import { Button } from '../components/ui/button';
import { ArrowLeft, Globe, MapPin, Building2, Wifi, BarChart3, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import ConnectivityProfile from '../components/ConnectivityProfile';
import { ClaimBadge } from '../components/BDAFDots';
import { LinksHub, ProductsSection, ReviewsSection, ShareProfileButton } from '../components/ProfileSections';
import { DataConfidencePanel, DataDisclaimer } from '../components/DataTrust';

export default function BusinessProfile() {
  const { id } = useParams();
  const [biz, setBiz] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = () => api.get(`/profile/${id}`).then(r => setProfile(r.data)).catch(() => {});
  useEffect(() => {
    api.get(`/businesses/${id}`).then(r => setBiz(r.data)).catch(console.error).finally(() => setLoading(false));
    loadProfile();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex items-center justify-center min-h-[400px] font-body text-sm text-ink/40">Loading...</div>;
  if (!biz) return <div className="flex items-center justify-center min-h-[400px] font-body text-sm text-status-red">Business not found</div>;

  const footprint = [
    { label: 'Website', value: biz.website, icon: Globe },
    { label: 'Google Business', value: biz.google_business, icon: MapPin },
    { label: 'Directory Listing', value: biz.directory_listing, icon: Building2 },
    { label: 'Social Media', value: biz.social_media, icon: BarChart3 },
    { label: 'Reviews', value: biz.reviews, icon: CheckCircle2 },
  ];

  const interventionType = biz.digital_score <= 1 ? 'Digital Skills Training' : biz.digital_score <= 3 ? 'Digital Adoption Support' : 'AI Readiness Assessment';

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-6 space-y-6" data-testid="business-profile">
      <Link to="/search">
        <Button variant="ghost" size="sm" className="font-body text-xs text-ink/60 rounded-[4px]">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Search
        </Button>
      </Link>

      {/* Header */}
      <div className="glass-panel p-6 animate-fade-in">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink">{biz.company_name}</h1>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <DigitalTierBadge tier={biz.digital_tier} />
              <ClaimBadge business={biz} size="md" stopPropagation={false} />
              {profile && <ShareProfileButton shareUrl={profile.share_url} className="!py-1 !px-3 !text-xs" />}
              <span className={`text-xs font-body px-2 py-0.5 rounded-[4px] border ${biz.ch_status === 'Active' ? 'bg-status-green/10 text-status-green border-status-green/20' : 'bg-status-red/10 text-status-red border-status-red/20'}`}>{biz.ch_status}</span>
              <span className="font-body text-xs text-ink/40">CH: {biz.ch_number}</span>
              <span className="font-body text-xs text-ink/40">SIC: {biz.sic_code}</span>
            </div>
          </div>
          <DigitalScoreRing score={biz.digital_score} size={80} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Companies House Data */}
        <div className="solid-card p-5 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <h3 className="font-display text-lg font-extrabold text-ink mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-info-blue" strokeWidth={1.5} /> Company Details
          </h3>
          <dl className="space-y-2 font-body text-sm">
            {[['Primary Business', 'primary_business'], ['Sector', 'sector'], ['Address', 'registered_address'], ['Postcode', 'postcode'], ['Trading Status', 'trading_status']].map(([label, f]) => {
              const c = profile?.company?.corrections?.[f];
              return (
                <div key={f} className="flex justify-between gap-2"><dt className="text-ink/50 shrink-0">{label}</dt><dd className={`text-ink text-right ${f === 'registered_address' ? 'text-xs max-w-[180px]' : ''}`}>{(c && profile.company[f]) || biz[f]}{c && <span className="ml-1 inline-flex px-1.5 py-0.5 rounded-full bg-status-green/10 text-status-green text-[10px] font-semibold align-middle" title={`Companies House record: ${c.original || '—'}${c.reason ? ` · ${c.reason}` : ''}`} data-testid={`corrected-${f}`}>Owner-corrected</span>}</dd></div>
              );
            })}
          </dl>
        </div>

        {/* Digital Footprint */}
        <div className="solid-card p-5 animate-fade-in" style={{ animationDelay: '0.15s' }}>
          <h3 className="font-display text-lg font-extrabold text-ink mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4 text-info-blue" strokeWidth={1.5} /> Digital Footprint
          </h3>
          <div className="space-y-2">
            {footprint.map(fp => (
              <div key={fp.label} className="flex items-center justify-between p-2 rounded-[4px] bg-ink/3">
                <span className="font-body text-sm text-ink/70 flex items-center gap-2">
                  <fp.icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {fp.label}
                </span>
                {fp.value ? (
                  <CheckCircle2 className="w-4 h-4 text-status-green" strokeWidth={1.5} />
                ) : (
                  <XCircle className="w-4 h-4 text-status-red" strokeWidth={1.5} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Connectivity & Context */}
        <div className="solid-card p-5 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <h3 className="font-display text-lg font-extrabold text-ink mb-3 flex items-center gap-2">
            <Wifi className="w-4 h-4 text-info-blue" strokeWidth={1.5} /> Connectivity
          </h3>
          <dl className="space-y-2 font-body text-sm">
            <div className="flex justify-between"><dt className="text-ink/50">Broadband Type</dt><dd className="text-ink">{biz.broadband_type}</dd></div>
            <div className="flex justify-between"><dt className="text-ink/50">Download Speed</dt><dd className="text-ink">{biz.download_speed} Mbps</dd></div>
            <div className="flex justify-between"><dt className="text-ink/50">IMD Decile</dt><dd className="text-ink">{biz.imd_decile}/10</dd></div>
            <div className="flex justify-between"><dt className="text-ink/50">District</dt><dd className="text-ink">{biz.postcode_district}</dd></div>
            <div className="flex justify-between"><dt className="text-ink/50">Confidence</dt><dd className="text-ink">{Math.round((biz.confidence_score || 0.8) * 100)}%</dd></div>
          </dl>
        </div>
      </div>

      {/* Connectivity Profile — full borough baseline + premise specifics */}
      <ConnectivityProfile business={biz} className="animate-fade-in" />

      {/* Online presence · Products & services · Reviews (shareable at /b/{id}) */}
      {profile && (
        <>
          <LinksHub links={profile.links} businessId={profile.id} claimed={profile.claimed} />
          <ProductsSection listings={profile.listings} claimed={profile.claimed} businessId={profile.id} isOwner={profile.is_owner} />
          <ReviewsSection businessId={profile.id} reviews={profile.reviews} summary={profile.review_summary} isOwner={profile.is_owner} onAdded={loadProfile} />
        </>
      )}

      {/* Intervention recommendation */}
      <div className="solid-card p-5 border-l-4 border-l-brand-orange bg-brand-orange/[0.03] animate-fade-in" style={{ animationDelay: '0.25s' }}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <h3 className="font-display text-lg font-extrabold text-ink">Recommended Intervention</h3>
            <p className="font-body text-sm text-ink/60 mt-1">{interventionType}</p>
            {biz.digital_tier === 'Digitally Invisible' && (
              <p className="font-body text-xs text-ink/40 mt-2 leading-relaxed">
                This business has no verified digital presence. It cannot be found online by potential customers. 
                This is an adoption gap, not an infrastructure gap — the broadband is there. The intervention needed is support, not infrastructure.
              </p>
            )}
            <Link to={`/interventions?district=${biz.postcode_district}`}>
              <Button size="sm" className="mt-3 bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-xs rounded-[4px]">
                Plan Intervention
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Data confidence + disclaimer */}
      {profile && <DataConfidencePanel data={profile.data_confidence} businessId={biz.id} claimed={profile.claimed} />}
      <DataDisclaimer business={biz} confidence={profile?.data_confidence} businessId={biz.id} claimed={!!profile?.claimed} />
    </div>
  );
}
